import { wahaFollowupsApi, integrationsApi } from '../api/misc';
import { downloadFile } from '../api/files';
import { whatsappService } from './whatsappService';
import { toast } from 'react-hot-toast';

interface FollowupRule {
    id: string;
    name: string;
    trigger_type: string;
    workflow_id: string;
    session_id: string;
    is_active: boolean;
    config: {
        sendingMode?: string;
        startTime?: string;
        stopTime?: string;
        sendingDelay?: number;
        audience?: Array<{
            name: string;
            phone: string;
            status: string;
        }>;
        days?: string[];
        timeSlots?: Array<{ start: string; end: string }>;
        dateStart?: string;
        dateEnd?: string;
    };
}

class FollowupScheduler {
    private intervalId: NodeJS.Timeout | null = null;
    private isRunning = false;
    private processingRules = new Set<string>();
    // Auth-failure backoff: when the backend rejects the token
    // (Invalid or expired token), stop polling instead of spamming
    // an error every 60s. User logging back in calls start() again.
    private authFailedAt: number | null = null;

    /**
     * Start the scheduler - checks every minute for rules to execute
     */
    start() {
        if (this.isRunning) {
            console.log('[FollowupScheduler] Already running');
            return;
        }

        console.log('[FollowupScheduler] Starting...');
        this.isRunning = true;
        this.authFailedAt = null;

        // Check immediately on start
        this.checkAndExecuteRules();

        // Then check every 60 seconds
        this.intervalId = setInterval(() => {
            this.checkAndExecuteRules();
        }, 60000); // 60 seconds
    }

    /**
     * Stop the scheduler
     */
    stop() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        this.isRunning = false;
        console.log('[FollowupScheduler] Stopped');
    }

    /**
     * Main function to check and execute eligible rules
     */
    private async checkAndExecuteRules() {
        try {
            // Fetch all active manual/manual_product rules
            const all = await wahaFollowupsApi.list();
            const rules = (all || []).filter((r: any) =>
                (r.is_active === true || r.is_active === 1) &&
                ['manual', 'manual_product'].includes(r.trigger_type)
            );
            if (!rules || rules.length === 0) return;

            console.log(`[FollowupScheduler] Found ${rules.length} active rules`);

            for (const rule of rules) {
                // Skip if already processing this rule
                if (this.processingRules.has(rule.id)) {
                    console.log(`[FollowupScheduler] Rule ${rule.name} is already being processed`);
                    continue;
                }

                // Check if rule should run now
                if (this.shouldRunNow(rule)) {
                    console.log(`[FollowupScheduler] Executing rule: ${rule.name}`);
                    this.executeRule(rule);
                }
            }
        } catch (err: any) {
            const msg = String(err?.message || err || '');
            // Quiet the loop on auth errors — token expired means
            // there's no point retrying every 60s. The single warn
            // tells the user once; start() resets the latch.
            if (/invalid or expired token|401|unauthor/i.test(msg)) {
                if (!this.authFailedAt) {
                    console.warn('[FollowupScheduler] Auth expired — pausing until next login (start()).');
                    this.authFailedAt = Date.now();
                }
                this.stop();
                return;
            }
            console.error('[FollowupScheduler] Error checking rules:', err);
        }
    }

    /**
     * Check if a rule should run based on its schedule config
     */
    private shouldRunNow(rule: FollowupRule): boolean {
        const config = rule.config;
        if (!config) return false;

        // Check if there's an audience to send to
        if (!config.audience || config.audience.length === 0) {
            return false;
        }

        // Check if there are pending contacts
        const hasPending = config.audience.some(a => a.status === 'pending');
        if (!hasPending) {
            return false; // All already sent
        }

        const now = new Date();
        const currentTime = now.toTimeString().slice(0, 5); // HH:MM
        const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });

        // Mode: Anytime - always run
        if (config.sendingMode === 'anytime') {
            return true;
        }

        // Mode: Schedule Time
        if (config.sendingMode === 'schedule_time') {
            if (!config.startTime || !config.stopTime) return false;
            return currentTime >= config.startTime && currentTime <= config.stopTime;
        }

        // Mode: Advanced Schedule
        if (config.sendingMode === 'advanced_schedule') {
            // Check date range
            if (config.dateStart) {
                const startDate = new Date(config.dateStart);
                if (now < startDate) return false;
            }
            if (config.dateEnd) {
                const endDate = new Date(config.dateEnd);
                endDate.setHours(23, 59, 59, 999);
                if (now > endDate) return false;
            }

            // Check days
            if (config.days && config.days.length > 0) {
                if (!config.days.includes(currentDay)) return false;
            }

            // Check time slots
            if (config.timeSlots && config.timeSlots.length > 0) {
                const inSlot = config.timeSlots.some(
                    slot => currentTime >= slot.start && currentTime <= slot.end
                );
                if (!inSlot) return false;
            }

            return true;
        }

        return false;
    }

    /**
     * Execute a rule - send messages to all pending contacts
     */
    private async executeRule(rule: FollowupRule) {
        this.processingRules.add(rule.id);

        try {
            // Load workflow
            const workflowBlob = await downloadFile(`workflows/${rule.workflow_id}.json`);

            const workflowText = await workflowBlob.text();
            const workflow = JSON.parse(workflowText);

            // Get pending contacts
            const pendingContacts = rule.config.audience?.filter(a => a.status === 'pending') || [];

            console.log(`[FollowupScheduler] Sending to ${pendingContacts.length} pending contacts`);

            const delay = (rule.config.sendingDelay ?? 5) * 1000; // Convert to ms

            for (const contact of pendingContacts) {
                try {
                    // Update status to 'sending'
                    await this.updateContactStatus(rule.id, contact.phone, 'sending');

                    // Send message
                    await this.sendWorkflowMessage(workflow, contact, rule.session_id);

                    // Update status to 'sent'
                    await this.updateContactStatus(rule.id, contact.phone, 'sent');

                    console.log(`[FollowupScheduler] Sent to ${contact.phone}`);

                    // Wait before next contact
                    if (delay > 0) {
                        await new Promise(resolve => setTimeout(resolve, delay));
                    }
                } catch (err) {
                    console.error(`[FollowupScheduler] Failed to send to ${contact.phone}:`, err);
                    await this.updateContactStatus(rule.id, contact.phone, 'failed');
                }
            }

            console.log(`[FollowupScheduler] Completed rule: ${rule.name}`);
        } catch (err) {
            console.error(`[FollowupScheduler] Error executing rule ${rule.name}:`, err);
        } finally {
            this.processingRules.delete(rule.id);
        }
    }

    private waService = whatsappService;

    // For testing
    public injectWhatsappService(service: any) {
        this.waService = service;
    }

    /**
     * Send workflow message to a contact
     */
    private async sendWorkflowMessage(workflow: any, contact: any, sessionId: string) {
        const nodes = workflow.graph?.nodes || [];
        const messageNode = nodes.find((n: any) => n.type === 'message');

        if (!messageNode) {
            throw new Error('No message node in workflow');
        }

        const messageData = messageNode.data;
        let messageText = messageData.value || '';

        // Replace {{customer.name}} with actual name
        messageText = messageText.replace(/\{\{customer\.name\}\}/g, contact.name || 'Customer');

        // Format phone
        let phone = contact.phone.replace(/\D/g, '');
        if (phone.startsWith('0')) {
            phone = '6' + phone;
        } else if (!phone.startsWith('60')) {
            phone = '60' + phone;
        }
        if (!phone.endsWith('@c.us')) phone = `${phone}@c.us`;

        const msgType = messageData.type;

        // Configure WAHA service
        const wahaRow: any = await integrationsApi.get('waha').catch(() => null);
        const wahaSettings = wahaRow?.settings;

        if (wahaSettings) {
            this.waService.configure({
                baseUrl: wahaSettings.apiUrl,
                apiKey: wahaSettings.apiKey
            });
        }

        // Send based on type
        if (msgType === 'reply_image' || msgType === 'reply_video' || msgType === 'reply_file') {
            if (!messageData.mediaUrl) throw new Error('Media URL missing');

            const mediaPayload = {
                session: sessionId,
                chatId: phone,
                text: messageText,
                file: {
                    url: messageData.mediaUrl,
                    filename: msgType === 'reply_image' ? 'image.jpg' : 'file',
                    mimetype: msgType === 'reply_image' ? 'image/jpeg' : 'application/octet-stream'
                }
            };

            if (msgType === 'reply_image') return await this.waService.sendImage(mediaPayload);
            if (msgType === 'reply_video') return await this.waService.sendVideo(mediaPayload);
            return await this.waService.sendFile(mediaPayload);
        } else {
            return await this.waService.sendText({
                session: sessionId,
                chatId: phone,
                text: messageText
            });
        }
    }

    /**
     * Update contact status in the rule config
     */
    private async updateContactStatus(ruleId: string, phone: string, status: string) {
        // Fetch current rule
        let rule: any = null;
        try {
            const all = await wahaFollowupsApi.list();
            rule = (all || []).find((r: any) => String(r.id) === String(ruleId));
        } catch {
            return;
        }

        if (!rule || !rule.config || !rule.config.audience) return;

        // Update the specific contact
        const updatedAudience = rule.config.audience.map((contact: any) =>
            contact.phone === phone ? { ...contact, status } : contact
        );

        // Save back
        await wahaFollowupsApi.update(ruleId, {
            config: { ...rule.config, audience: updatedAudience },
            updated_at: new Date()
        });
    }
}

// Singleton instance
export const followupScheduler = new FollowupScheduler();
